import { createSignal } from "solid-js";

export type Theme = "light" | "dark";

const STORAGE_KEY = "conatus.theme";

const canAccessBrowser = () => {
  return typeof window !== "undefined" && typeof document !== "undefined";
};

const canAccessStorage = () => {
  return canAccessBrowser() && "localStorage" in window;
};

const [themeValue, setThemeValue] = createSignal<Theme>("dark");

function isValidTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark";
}

function getStoredTheme(): Theme | null {
  if (!canAccessStorage()) return null;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isValidTheme(stored) ? stored : null;
  } catch {
    return null;
  }
}

function persistTheme(next: Theme): void {
  if (!canAccessStorage()) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Storage can be unavailable even when the API exists.
  }
}

function getSystemTheme(): Theme {
  if (!canAccessBrowser() || typeof window.matchMedia !== "function") {
    return "dark";
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(theme: Theme): void {
  if (!canAccessBrowser()) return;

  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  root.dataset.theme = theme;
}

/**
 * Initialize the theme synchronously.
 * Should be called before rendering the Solid app.
 * Fallback order: stored valid theme -> system preference -> "dark"
 */
export function initTheme(): Theme {
  const next = getStoredTheme() ?? getSystemTheme();
  setThemeValue(next);
  applyTheme(next);
  return next;
}

/**
 * Get the current theme value (synchronously).
 */
export function getTheme(): Theme {
  return themeValue();
}

/**
 * Set the theme, persist it, and update the DOM.
 */
export function setTheme(next: Theme): void {
  if (!isValidTheme(next)) return;

  persistTheme(next);
  setThemeValue(next);
  applyTheme(next);
}

/**
 * Toggle between light and dark theme.
 */
export function toggleTheme(): Theme {
  const current = getTheme();
  const next: Theme = current === "dark" ? "light" : "dark";
  setTheme(next);
  return next;
}

export function theme(): Theme {
  return themeValue();
}

